import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import AdminLayout from "../layouts/adminLayout/AdminLayout";
import {
  createCityCorporation,
  deleteCityCorporation,
  getCityCorporations,
  getDivisions,
  normalizeList,
  updateCityCorporation,
} from "../config/api/locationApi";

const emptyForm = {
  division_id: "",
  name: "",
  bn_name: "",
  url: "",
};

export default function CityCorporationList() {
  const [rows, setRows] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [filterDivisionId, setFilterDivisionId] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const fetchDivisions = useCallback(async () => {
    try {
      const res = await getDivisions();
      setDivisions(normalizeList(res));
    } catch (error) {
      console.error(error);
      toast.error("Failed to load divisions");
    }
  }, []);

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getCityCorporations({
        division_id: filterDivisionId || undefined,
        search: appliedSearch || undefined,
      });
      setRows(normalizeList(res));
    } catch (error) {
      console.error(error);
      toast.error("Failed to load city corporations");
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, filterDivisionId]);

  useEffect(() => {
    fetchDivisions();
  }, [fetchDivisions]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const handleSearch = () => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setAppliedSearch(search);
  };

  const handleOpenCreate = () => {
    setEditId(null);
    setFormData(emptyForm);
    setOpenModal(true);
  };

  const handleOpenEdit = (row) => {
    setEditId(row.id);
    setFormData({
      division_id: row.division_id || "",
      name: row.name || "",
      bn_name: row.bn_name || "",
      url: row.url || "",
    });
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditId(null);
    setFormData(emptyForm);
  };

  const handleSubmit = async () => {
    try {
      if (!formData.division_id || !formData.name.trim()) {
        toast.error("Division and name are required");
        return;
      }

      const payload = {
        division_id: formData.division_id,
        name: formData.name,
        bn_name: formData.bn_name,
        url: formData.url,
      };

      if (editId) {
        await updateCityCorporation({ id: editId, ...payload });
        toast.success("City corporation updated successfully");
      } else {
        await createCityCorporation(payload);
        toast.success("City corporation created successfully");
      }

      handleCloseModal();
      fetchRows();
    } catch (error) {
      console.error(error);
      toast.error("Failed to save city corporation");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure to delete?")) return;

    try {
      await deleteCityCorporation(id);
      toast.success("City corporation deleted successfully");
      fetchRows();
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete city corporation");
    }
  };

  const columns = [
    { field: "id", headerName: "ID", width: 80 },
    { field: "name", headerName: "Name", width: 180 },
    {
      field: "bn_name",
      headerName: "Bangla Name",
      width: 180,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "division_id",
      headerName: "Division",
      width: 180,
      renderCell: (params) =>
        divisions.find((item) => String(item.id) === String(params.value))
          ?.name ||
        params.value ||
        "-",
    },
    {
      field: "url",
      headerName: "URL",
      width: 180,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "action",
      headerName: "Action",
      width: 200,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box className="flex gap-2">
          <Button
            size="small"
            variant="contained"
            onClick={() => handleOpenEdit(params.row)}
          >
            Edit
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={() => handleDelete(params.row.id)}
          >
            Delete
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <AdminLayout title="City Corporation">
      <Box className="p-6 bg-white !rounded">
        <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-4">
          <TextField
            label="Search"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <Select
            size="small"
            displayEmpty
            value={filterDivisionId}
            onChange={(e) => {
              setFilterDivisionId(e.target.value);
              setPaginationModel((prev) => ({ ...prev, page: 0 }));
            }}
          >
            <MenuItem value="">All Divisions</MenuItem>
            {divisions.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.name}
              </MenuItem>
            ))}
          </Select>

          <Button variant="contained" onClick={handleSearch}>
            Search
          </Button>
          <Button variant="outlined" onClick={handleOpenCreate}>
            Add City Corporation
          </Button>
        </div>

        <div style={{ height: 600, width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            loading={loading}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 20, 50]}
            disableRowSelectionOnClick
          />
        </div>

        <Dialog
          open={openModal}
          onClose={handleCloseModal}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            {editId ? "Edit City Corporation" : "Add City Corporation"}
          </DialogTitle>
          <DialogContent>
            <Box className="grid grid-cols-1 gap-4 mt-3">
              <Select
                size="small"
                displayEmpty
                value={formData.division_id}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    division_id: e.target.value,
                  }))
                }
              >
                <MenuItem value="">Select Division</MenuItem>
                {divisions.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>

              <TextField
                label="City Corporation Name"
                size="small"
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
              />

              <TextField
                label="Bangla Name"
                size="small"
                value={formData.bn_name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, bn_name: e.target.value }))
                }
              />

              <TextField
                label="URL"
                size="small"
                value={formData.url}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, url: e.target.value }))
                }
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal}>Cancel</Button>
            <Button variant="contained" onClick={handleSubmit}>
              {editId ? "Update" : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
}
